import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/9fe16284-c883-46c5-9976-fb4189ef86a2/images/4414b575521af6b937b0721d2c61060a2fec8b4fac41d65ab6fa293761654c61.png";

export default function PrivacyPage() {
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-viraly-dark">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-card border-0 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Viraly" className="h-10 w-10" />
            <span className="font-heading font-bold text-2xl gradient-text">Viraly</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-viraly-muted hover:text-white flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-24 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-viraly-orange/20 flex items-center justify-center">
                <Shield className="w-6 h-6 text-viraly-orange" />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-bold">Privacy Policy</h1>
                <p className="text-viraly-muted">Last Updated: March 20, 2026</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card-base p-8 prose prose-invert max-w-none"
          >
            <p className="text-viraly-muted mb-6">
              Viraly ("we", "us", "our") is committed to protecting your privacy in accordance with the Personal Information Protection and Electronic Documents Act (PIPEDA) and applicable Canadian privacy laws.
            </p>

            {/* Compliance Badges Section */}
            <div className="bg-white/5 rounded-xl p-6 mb-8 border border-white/10">
              <h3 className="text-lg font-semibold text-viraly-light mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-viraly-success" />
                Our Privacy Compliance
              </h3>
              <p className="text-viraly-muted text-sm mb-4">
                Viraly is compliant with major international privacy regulations:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-blue-500/20">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <span className="text-blue-400 font-bold text-sm">EU</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">GDPR</p>
                    <p className="text-xs text-viraly-muted">EU Data Protection</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-yellow-500/20">
                  <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <span className="text-yellow-400 font-bold text-sm">CA</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">CCPA</p>
                    <p className="text-xs text-viraly-muted">California Privacy</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-red-500/20">
                  <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                    <span className="text-red-400 font-bold text-sm">🍁</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">PIPEDA</p>
                    <p className="text-xs text-viraly-muted">Canadian Privacy</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-green-500/20">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <span className="text-green-400 font-bold text-sm">BR</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">LGPD</p>
                    <p className="text-xs text-viraly-muted">Brazil Privacy</p>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">1. INFORMATION WE COLLECT</h2>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">1.1 Information You Provide</h3>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li><strong className="text-viraly-light">Account Information:</strong> Name, email address, password</li>
              <li><strong className="text-viraly-light">Profile Information:</strong> Business name, social media handles, preferences</li>
              <li><strong className="text-viraly-light">Brand Information:</strong> Website URLs (for brand learning), brand voice, target audience, keywords, headshot/logo images, contact information (phone, email, tagline, credentials)</li>
              <li><strong className="text-viraly-light">Content Data:</strong> Text, images, videos, and URLs you submit for content creation, including multi-image uploads for collage creation</li>
              <li><strong className="text-viraly-light">Payment Information:</strong> Payment details (processed securely through PayPal)</li>
              <li><strong className="text-viraly-light">Voice Recordings:</strong> Audio files submitted for voice-to-text transcription</li>
              <li><strong className="text-viraly-light">Communications:</strong> Messages exchanged through our platform, support requests</li>
              <li><strong className="text-viraly-light">Template Preferences:</strong> Your favorite templates and custom text styling preferences (fonts, colors, sizes)</li>
            </ul>

            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">1.2 Information Collected Automatically</h3>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>Device information and browser type</li>
              <li>IP address and approximate location</li>
              <li>Pages visited and features used</li>
              <li>Date and time of visits</li>
              <li>Referral sources</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">1.3 Social Media Engagement Data</h3>
            <p className="text-viraly-muted mb-4">
              When you connect your social media accounts (Facebook, Instagram, TikTok, Pinterest), we collect engagement metrics for posts published through our platform:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>Likes, reactions, and favorites</li>
              <li>Comments and replies</li>
              <li>Shares and reposts</li>
              <li>Reach and impressions (where available)</li>
            </ul>
            <p className="text-viraly-muted mb-4">
              This data is synced every 6 hours and used to provide AI-powered performance insights and content recommendations. You can revoke this access at any time by disconnecting your social accounts.
            </p>

            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">1.4 Past Posts and Content Analysis (Brand Learning)</h3>
            <p className="text-viraly-muted mb-4">
              With your consent during registration, we may access your existing/past posts on connected social media accounts (Facebook Pages and Instagram Business accounts) to:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li><strong className="text-viraly-light">Learn Your Brand Voice:</strong> Analyze your writing style, tone, and content patterns</li>
              <li><strong className="text-viraly-light">Understand Your Audience:</strong> Identify what content performs best with your followers</li>
              <li><strong className="text-viraly-light">Optimize Posting Times:</strong> Determine when your audience is most active</li>
              <li><strong className="text-viraly-light">Personalize AI Content:</strong> Generate content that matches your established brand identity</li>
            </ul>
            <p className="text-viraly-muted mb-4">
              <strong className="text-viraly-light">Important:</strong> This analysis is performed solely to improve the quality of AI-generated content for YOUR account. We do not share this data with other users or third parties. You can request deletion of this learned data at any time by contacting support@viraly.io or through your Account Settings.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">2. HOW WE USE YOUR INFORMATION</h2>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>To create and manage your account</li>
              <li>To provide AI-powered content generation services</li>
              <li>To schedule and publish content to social media platforms</li>
              <li>To process payments and transactions</li>
              <li>To send service-related notifications (post updates, billing reminders)</li>
              <li>To improve our platform and user experience</li>
              <li>To provide customer support</li>
              <li>To comply with legal obligations</li>
              <li>To detect and prevent fraud</li>
            </ul>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">3. INFORMATION SHARING</h2>
            <p className="text-viraly-light mb-4">
              We share your information only in the following circumstances:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li><strong className="text-viraly-light">Social Media Platforms:</strong> To publish your content on Facebook, Instagram, and TikTok (with your authorization)</li>
              <li><strong className="text-viraly-light">Payment Processors:</strong> PayPal processes payment information securely</li>
              <li><strong className="text-viraly-light">AI Service Providers:</strong> We use third-party AI services to generate content (data is processed securely)</li>
              <li><strong className="text-viraly-light">Gift Recipients:</strong> When you send a Gift Pack, we share your name and optional message with the recipient via email</li>
              <li><strong className="text-viraly-light">Legal Requirements:</strong> When required by law, court order, or government request</li>
              <li><strong className="text-viraly-light">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
            
            <div className="p-4 rounded-xl bg-viraly-success/10 border border-viraly-success/20 my-6">
              <p className="text-viraly-light font-medium mb-2">We DO NOT:</p>
              <ul className="list-disc list-inside text-viraly-muted space-y-1">
                <li>Sell your personal information to third parties</li>
                <li>Share your information for third-party marketing</li>
                <li>Use your data for purposes unrelated to our services</li>
              </ul>
            </div>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">4. DATA SECURITY</h2>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>We use industry-standard encryption (SSL/TLS) for data transmission</li>
              <li>Passwords are hashed and never stored in plain text</li>
              <li>Payment information is processed by PCI-compliant payment processors</li>
              <li>Access to personal data is restricted to authorized personnel</li>
              <li>We conduct regular security assessments</li>
              <li>WebAuthn biometric authentication available for enhanced security</li>
            </ul>
            
            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">4.1 DATA RETENTION</h2>
            <p className="text-viraly-muted mb-4">
              We retain different types of data for different periods:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li><strong className="text-viraly-light">Generated Content (images, videos):</strong> 90 days from creation. Users should download content they wish to keep permanently.</li>
              <li><strong className="text-viraly-light">Account Information:</strong> Retained while your account is active, deleted within 30 days of account closure upon request.</li>
              <li><strong className="text-viraly-light">Financial Records:</strong> 7 years in compliance with Canadian tax regulations (Income Tax Act).</li>
              <li><strong className="text-viraly-light">Engagement Analytics:</strong> 12 months from sync date.</li>
              <li><strong className="text-viraly-light">Support Communications:</strong> 2 years from last interaction.</li>
            </ul>
            <p className="text-viraly-muted mb-4">
              Content purging occurs automatically and cannot be reversed. We recommend downloading any content you wish to retain before the 90-day period expires.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">5. YOUR RIGHTS UNDER PIPEDA</h2>
            <p className="text-viraly-light mb-4">
              As a Canadian resident, you have the right to:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li><strong className="text-viraly-light">Access:</strong> Request a copy of your personal information</li>
              <li><strong className="text-viraly-light">Correction:</strong> Request correction of inaccurate information</li>
              <li><strong className="text-viraly-light">Withdrawal of Consent:</strong> Withdraw consent for certain data uses</li>
              <li><strong className="text-viraly-light">Deletion:</strong> Request deletion of your account and associated data</li>
              <li><strong className="text-viraly-light">Complaint:</strong> File a complaint with the Privacy Commissioner of Canada</li>
            </ul>
            <p className="text-viraly-muted mb-4">
              To exercise these rights, contact us at <a href="mailto:care@viraly.ca" className="text-viraly-orange hover:underline">care@viraly.ca</a>
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">6. DATA RETENTION</h2>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li><strong className="text-viraly-light">Active account data:</strong> Retained while your account is active</li>
              <li><strong className="text-viraly-light">Transaction records:</strong> Retained for 7 years for tax and legal purposes</li>
              <li><strong className="text-viraly-light">Deleted accounts:</strong> Data removed within 30 days, except where legally required</li>
              <li><strong className="text-viraly-light">Generated content:</strong> Retained until you delete it or close your account</li>
            </ul>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">7. COOKIES AND TRACKING</h2>
            <p className="text-viraly-light mb-4">
              We use cookies and similar technologies to:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>Keep you logged in</li>
              <li>Remember your preferences</li>
              <li>Analyze site usage and performance</li>
              <li>Improve our services</li>
            </ul>
            <p className="text-viraly-muted mb-4">
              You can control cookies through your browser settings.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">8. THIRD-PARTY INTEGRATIONS</h2>
            <p className="text-viraly-muted mb-4">
              When you connect your social media accounts (Facebook, Instagram, TikTok), we access only the permissions necessary to publish content on your behalf. We do not access private messages or non-public content.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">9. AUTO-POSTING CONSENT</h2>
            <p className="text-viraly-muted mb-4">
              By using our scheduling features, you consent to:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li><strong className="text-viraly-light">Automatic Publishing:</strong> Content in your scheduled queue will be automatically posted to your connected social accounts at the designated time</li>
              <li><strong className="text-viraly-light">Content Storage:</strong> Your scheduled content is stored securely until published</li>
              <li><strong className="text-viraly-light">Platform Access:</strong> We will access your social media accounts solely to publish approved content</li>
            </ul>
            <p className="text-viraly-muted mb-4">
              You can withdraw consent at any time by deleting scheduled posts or disconnecting your social accounts.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">10. LOYALTY AND REFERRAL PROGRAMS</h2>
            <p className="text-viraly-muted mb-4">
              If you participate in our loyalty or referral programs, we collect and process:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li><strong className="text-viraly-light">Purchase History:</strong> To calculate your loyalty tier based on rolling 12-month spending</li>
              <li><strong className="text-viraly-light">Referral Data:</strong> Who you referred and their purchase activity (aggregated, not individual details)</li>
              <li><strong className="text-viraly-light">Payout Information:</strong> For influencers, e-Transfer or PayPal email for commission payments</li>
            </ul>
            <p className="text-viraly-muted mb-4">
              Your loyalty tier and referral statistics may be displayed to you in your Rewards Center.
            </p>

            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">10.1 Influencer Program Publicity</h3>
            <p className="text-viraly-muted mb-4">
              By participating in our Influencer Program, you consent to Viraly displaying the following information publicly on our website, landing pages, and marketing materials:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>Your first name and last initial (e.g., "Sarah M.")</li>
              <li>Your general location (city and country)</li>
              <li>Your total earnings from the Influencer Program</li>
              <li>Your projected monthly average earnings (estimated based on your activity period)</li>
              <li>Your total number of successful referrals</li>
            </ul>
            <p className="text-viraly-muted mb-4">
              <strong className="text-viraly-light">Note on Monthly Earnings Estimates:</strong> Monthly average earnings displayed publicly are calculated estimates based on total earnings divided by the time period of activity. These figures are projections and may not reflect actual month-to-month variations in referral activity.
            </p>
            <p className="text-viraly-muted mb-4">
              <strong className="text-viraly-light">No other personal information will be shared.</strong> This data is displayed solely for promotional purposes to showcase our community's success and inspire others. If you wish to opt out of public display, please contact us at <a href="mailto:care@viraly.ca" className="text-viraly-orange hover:underline">care@viraly.ca</a>. Note that opting out may affect your continued eligibility for the Influencer Program.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">11. STOCK PHOTOS AND THIRD-PARTY CONTENT</h2>
            <p className="text-viraly-muted mb-4">
              In providing our services, we may utilize stock photographs from third-party providers (including Unsplash, Pexels, and similar services). Regarding this content:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li><strong className="text-viraly-light">No Personal Data Collection:</strong> We do not collect personal information about individuals depicted in stock photographs</li>
              <li><strong className="text-viraly-light">No Representation:</strong> The use of stock images does not imply any endorsement, affiliation, or representation of the subjects depicted</li>
              <li><strong className="text-viraly-light">User Responsibility:</strong> Users are responsible for ensuring appropriate use of selected images, particularly those depicting people, religious sites, cultural elements, or sensitive subjects</li>
              <li><strong className="text-viraly-light">Third-Party Licenses:</strong> Stock images are used under their respective provider licenses and may be subject to their terms of use</li>
            </ul>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">12. PUBLISHED CONTENT DISCLAIMER</h2>
            <p className="text-viraly-muted mb-4">
              Content published through Viraly to your social media accounts:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li><strong className="text-viraly-light">User Ownership:</strong> Remains the responsibility of the user who created and approved it</li>
              <li><strong className="text-viraly-light">No Endorsement:</strong> Viraly does not endorse any views, opinions, or claims expressed in user-published content</li>
              <li><strong className="text-viraly-light">No Liability:</strong> We are not liable for any consequences arising from content you publish through our platform</li>
              <li><strong className="text-viraly-light">Cultural Sensitivity:</strong> Users must ensure content respects cultural, religious, and ethnic sensitivities in their target markets</li>
            </ul>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">13. CHILDREN'S PRIVACY</h2>
            <p className="text-viraly-muted mb-4">
              Viraly is not intended for users under 18 years of age. We do not knowingly collect personal information from children. If we become aware that we have collected data from a minor, we will delete it immediately.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">14. INTERNATIONAL DATA TRANSFERS</h2>
            <p className="text-viraly-muted mb-4">
              Your data may be processed by AI service providers and cloud infrastructure located outside Canada. We ensure appropriate safeguards are in place to protect your information in accordance with Canadian privacy laws.
            </p>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">14.1 Transfer Mechanisms</h3>
            <p className="text-viraly-muted mb-4">
              For international data transfers, we use:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li><strong className="text-viraly-light">Standard Contractual Clauses (SCCs):</strong> EU-approved data transfer agreements</li>
              <li><strong className="text-viraly-light">Adequacy Decisions:</strong> Transfers to countries recognized as providing adequate protection</li>
              <li><strong className="text-viraly-light">Binding Corporate Rules:</strong> For transfers within our service provider network</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">14.2 Data Processing Locations</h3>
            <p className="text-viraly-muted mb-4">
              Primary data processing occurs in Canada and the United States. Enterprise customers may request a Data Processing Agreement (DPA) specifying data handling requirements.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">15. GLOBAL PRIVACY RIGHTS</h2>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">15.1 European Economic Area (GDPR)</h3>
            <p className="text-viraly-muted mb-4">
              If you are located in the EEA, you have additional rights under GDPR:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li><strong className="text-viraly-light">Right to Access:</strong> Obtain a copy of your personal data</li>
              <li><strong className="text-viraly-light">Right to Rectification:</strong> Correct inaccurate data</li>
              <li><strong className="text-viraly-light">Right to Erasure:</strong> Request deletion ("right to be forgotten")</li>
              <li><strong className="text-viraly-light">Right to Restrict Processing:</strong> Limit how we use your data</li>
              <li><strong className="text-viraly-light">Right to Data Portability:</strong> Receive your data in a machine-readable format</li>
              <li><strong className="text-viraly-light">Right to Object:</strong> Object to processing based on legitimate interests</li>
              <li><strong className="text-viraly-light">Right to Withdraw Consent:</strong> Withdraw consent at any time</li>
            </ul>
            <p className="text-viraly-muted mb-4">
              To exercise these rights, contact our Data Protection contact at care@viraly.ca. You may also lodge a complaint with your local supervisory authority.
            </p>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">15.2 California Residents (CCPA/CPRA)</h3>
            <p className="text-viraly-muted mb-4">
              California residents have the following rights:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li><strong className="text-viraly-light">Right to Know:</strong> What personal information we collect and how it's used</li>
              <li><strong className="text-viraly-light">Right to Delete:</strong> Request deletion of personal information</li>
              <li><strong className="text-viraly-light">Right to Opt-Out:</strong> Opt out of the sale of personal information (we do NOT sell data)</li>
              <li><strong className="text-viraly-light">Right to Non-Discrimination:</strong> Equal service regardless of exercising privacy rights</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">15.3 Brazil Residents (LGPD)</h3>
            <p className="text-viraly-muted mb-4">
              Brazilian users have rights under the Lei Geral de Proteção de Dados (LGPD), including access, correction, anonymization, data portability, and the right to information about data sharing.
            </p>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">15.4 Other Jurisdictions</h3>
            <p className="text-viraly-muted mb-4">
              We respect privacy rights in all jurisdictions where we operate. If you have specific questions about your local privacy rights, please contact care@viraly.ca.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">16. B2B DATA PROCESSING</h2>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">16.1 Enterprise Customers</h3>
            <p className="text-viraly-muted mb-4">
              For B2B Enterprise customers, Viraly acts as a data processor on behalf of the Enterprise (data controller). Enterprise customers can request:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>Data Processing Agreement (DPA) execution</li>
              <li>Subprocessor lists and updates</li>
              <li>Security audit reports and certifications</li>
              <li>Data location specifications</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">16.2 Merchant Data</h3>
            <p className="text-viraly-muted mb-4">
              Merchants onboarded by Enterprises are subject to their Enterprise's privacy policies in addition to this Privacy Policy. Enterprises are responsible for informing their Merchants about data handling practices.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">17. CHANGES TO THIS POLICY</h2>
            <p className="text-viraly-muted mb-4">
              We may update this Privacy Policy periodically. We will notify you of significant changes via email or platform notification. Continued use after changes constitutes acceptance.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">18. CONTACT US</h2>
            <p className="text-viraly-muted mb-4">
              For privacy-related questions or to exercise your rights:
            </p>
            <div className="p-4 rounded-xl bg-white/5 border border-white/10 mb-4">
              <p className="text-viraly-light font-medium">Viraly</p>
              <p className="text-viraly-muted">Email: <a href="mailto:care@viraly.ca" className="text-viraly-orange hover:underline">care@viraly.ca</a></p>
              <p className="text-viraly-muted">Website: <a href="https://viraly.ca" className="text-viraly-orange hover:underline">viraly.ca</a></p>
            </div>
            <div className="p-4 rounded-xl bg-white/5 border border-white/10 mb-4">
              <p className="text-viraly-light font-medium">Office of the Privacy Commissioner of Canada</p>
              <p className="text-viraly-muted">Website: <a href="https://priv.gc.ca" className="text-viraly-orange hover:underline" target="_blank" rel="noopener noreferrer">priv.gc.ca</a></p>
              <p className="text-viraly-muted">Phone: 1-800-282-1376</p>
            </div>

            <div className="mt-8 pt-6 border-t border-white/10">
              <p className="text-viraly-muted text-sm">
                By using Viraly, you acknowledge that you have read and understood this Privacy Policy.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Viraly" className="h-6 w-6" />
            <span className="font-heading font-bold">Viraly</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-viraly-muted">
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            <a href="mailto:care@viraly.ca" className="hover:text-white transition-colors">Contact</a>
          </div>
          <p className="text-viraly-muted text-sm">© 2026 Viraly</p>
        </div>
      </footer>
    </div>
  );
}
